import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Mail, ClipboardList, MessageSquareText, MessageCircle, ShieldCheck,
  Building2, Workflow, Search, Sparkles, ArrowRight, Inbox, BrainCircuit, CheckCheck,
  Megaphone, Vote, Home, Landmark,
} from "lucide-react";

const STEPS = [
  { icon: Inbox, title: "Ricevi", desc: "Email e PEC arrivano in un unico posto." },
  { icon: BrainCircuit, title: "L'AI elabora", desc: "Legge, capisce, classifica, apre le pratiche e valuta l'urgenza." },
  { icon: CheckCheck, title: "Tu decidi", desc: "Approvi le bozze, assegni i fornitori e condividi con i condòmini." },
];

const FEATURES = [
  { icon: Mail, color: "text-sky-500", title: "Posta & PEC intelligente", desc: "L'AI legge email e allegati (PDF, Excel, immagini), li riassume e li classifica." },
  { icon: ClipboardList, color: "text-purple-500", title: "Pratiche AI", desc: "Apre automaticamente le pratiche, rileva le emergenze e propone i fornitori giusti." },
  { icon: MessageSquareText, color: "text-[#007BFF]", title: "Chat AI operativa", desc: "Chiedi in linguaggio naturale: l'AI conosce anche avvisi e sondaggi del condominio ed esegue azioni per te." },
  { icon: Megaphone, color: "text-red-500", title: "Bacheca Avvisi", desc: "Pubblica avvisi mirati (anche 'in evidenza'), con allegati, programmazione e conferme di lettura." },
  { icon: Vote, color: "text-teal-500", title: "Sondaggi e votazioni", desc: "Crea sondaggi per i condòmini: un voto a testa, scadenza e risultati in tempo reale." },
  { icon: Home, color: "text-emerald-500", title: "Stima immobili (AVM)", desc: "Valutazione automatica del valore di mercato con confronto di zona su dati OMI. (Premium)" },
  { icon: Landmark, color: "text-blue-600", title: "Sincronizzazione catastale", desc: "Scarica i dati catastali degli immobili e li abbina ai condòmini. (Premium)" },
  { icon: MessageCircle, color: "text-emerald-500", title: "Notifiche SMS", desc: "Invia le credenziali dei portali (Premium/Elite) e comunica nelle pratiche via SMS (Elite), con avvisi e sondaggi notificabili via SMS." },
  { icon: ShieldCheck, color: "text-pink-500", title: "Portali Condòmini", desc: "Ogni condòmino accede al suo portale: documenti, avvisi, sondaggi, segnalazioni e il suo immobile." },
  { icon: Building2, color: "text-indigo-500", title: "Gestione condomìni & Danea", desc: "Importa condomìni e anagrafiche con un semplice trascinamento del file." },
  { icon: Workflow, color: "text-amber-500", title: "Workflow & Automazioni", desc: "Regole automatiche per instradare, rispondere e creare pratiche. Le azioni sensibili restano da approvare." },
  { icon: Search, color: "text-cyan-500", title: "Ricerca semantica", desc: "Trova qualsiasi comunicazione ponendo semplicemente una domanda." },
];

export const WelcomeDialog = ({ open, onClose }) => {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-3xl p-0 overflow-hidden gap-0 max-h-[92vh] overflow-y-auto"
        data-testid="welcome-dialog"
      >
        <div className="relative">
          <img src="/welcome-hero.jpg" alt="Sedio AI" className="w-full h-44 sm:h-52 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#03102a] via-[#03102a]/70 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7dbcff] mb-1.5">
              <Sparkles className="h-3.5 w-3.5" />Benvenuto in Sedio
            </div>
            <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight">
              Il tuo Condominio AI
            </h2>
          </div>
        </div>

        <div className="p-6 space-y-7">
          <p className="text-sm text-muted-foreground -mt-1">
            Sedio automatizza la gestione del condominio: raccoglie le comunicazioni, le comprende con l'intelligenza
            artificiale e trasforma ogni richiesta in un'azione concreta. Ecco cosa puoi fare.
          </p>

          {/* Come funziona */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#007BFF] font-semibold mb-3">Come funziona</div>
            <div className="flex flex-col sm:flex-row items-stretch gap-3">
              {STEPS.map((s, i) => (
                <div key={s.title} className="flex-1 flex items-center gap-3">
                  <div className="flex-1 bg-secondary/60 rounded-xl p-4 h-full" style={{ animation: `popIn 0.4s ${i * 0.12}s cubic-bezier(0.34,1.56,0.64,1) both` }}>
                    <s.icon className="h-6 w-6 text-[#007BFF] mb-2" strokeWidth={2} />
                    <div className="font-semibold text-sm">{s.title}</div>
                    <div className="text-[12px] text-muted-foreground mt-0.5">{s.desc}</div>
                  </div>
                  {i < STEPS.length - 1 && <ArrowRight className="h-5 w-5 text-muted-foreground/50 shrink-0 hidden sm:block" />}
                </div>
              ))}
            </div>
          </div>

          {/* Funzioni AI */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#007BFF] font-semibold mb-3">Tutte le funzioni con l'AI</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="welcome-features">
              {FEATURES.map((f, i) => (
                <div key={f.title}
                  className="flex items-start gap-3 rounded-xl border border-border p-3.5 hover:border-[#007BFF]/40 hover:shadow-sm transition-all"
                  style={{ animation: `fadeInUp 0.45s ${0.1 + i * 0.05}s both` }}>
                  <div className="shrink-0 h-9 w-9 rounded-lg bg-secondary flex items-center justify-center">
                    <f.icon className={`h-5 w-5 ${f.color}`} strokeWidth={2} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-[13px]">{f.title}</div>
                    <div className="text-[12px] text-muted-foreground mt-0.5 leading-snug">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1">
            <p className="text-[12px] text-muted-foreground">Potrai esplorare tutto con calma dal menu laterale.</p>
            <Button data-testid="welcome-start-btn" onClick={onClose}
              className="w-full sm:w-auto bg-[#007BFF] hover:bg-[#0066d6] rounded-full px-8 h-11 font-semibold">
              Inizia ora <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
